import { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Eye,
  X,
  ToggleLeft,
  ToggleRight,
  Briefcase,
  Building2,
  Clock,
  Users,
} from "lucide-react";
import { showError, showSuccess } from "../lib/toast";
import {
  server_post_data,
  get_case_study_data,
  manage_case_study_status,
  delete_case_study,
} from "../serviceconnection/ServiceConnection";
import CaseStudyForm from "./CaseStudyForm";

export default function CaseStudies() {
  const [cases, setCases] = useState([]);
  const [search, setSearch] = useState("");
  const [industryFilter, setIndustryFilter] = useState("All");
  const [listLoading, setListLoading] = useState(true);

  // view modal
  const [viewCase, setViewCase] = useState(null);
  // delete modal
  const [deleteId, setDeleteId] = useState(null);

  // ── FORM PAGE STATE ──────────────────────────────────────────────────────
  // { open: false } = list page
  // { open: true, editId: null } = add new
  // { open: true, editId: 5 } = edit existing
  const [formPage, setFormPage] = useState({ open: false, editId: null });

  // ── Derived ──────────────────────────────────────────────────────────────
  const industries = [
    "All",
    ...Array.from(new Set(cases.map((c) => c.industry).filter(Boolean))),
  ];

  const filtered = cases.filter((c) => {
    const matchSearch =
      c.title?.toLowerCase().includes(search.toLowerCase()) ||
      c.industry?.toLowerCase().includes(search.toLowerCase()) ||
      c.solution?.toLowerCase().includes(search.toLowerCase());
    const matchInd = industryFilter === "All" || c.industry === industryFilter;
    return matchSearch && matchInd;
  });

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchCases = async () => {
    try {
      setListLoading(true);
      const fd = new FormData();
      fd.append("main_id", 0);
      const res = await server_post_data(get_case_study_data, fd);
      setCases(res.data?.data ?? []);
    } catch {
      showError("Failed to load case studies");
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchCases();
  }, []);

  // ── Toggle ────────────────────────────────────────────────────────────────
  const handleToggle = async (c) => {
    try {
      const fd = new FormData();
      fd.append("p_id", c.primary_id);
      fd.append("status", c.status === 1 ? "0" : "1");
      const res = await server_post_data(manage_case_study_status, fd);
      if (res.data?.error) {
        showError(res.data?.message);
        return;
      }
      showSuccess(res.data?.message);
      setCases((prev) =>
        prev.map((item) =>
          item.primary_id === c.primary_id
            ? { ...item, status: c.status === 1 ? 0 : 1 }
            : item,
        ),
      );
    } catch {
      showError("Something went wrong");
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    try {
      const fd = new FormData();
      fd.append("p_id", deleteId);
      const res = await server_post_data(delete_case_study, fd);
      if (res.data?.error) {
        showError(res.data?.message);
        return;
      }
      showSuccess("Case study deleted");
      setDeleteId(null);
      setCases((prev) => prev.filter((c) => c.primary_id !== deleteId));
    } catch {
      showError("Something went wrong");
    }
  };

  // ── If form page is open, render it instead ───────────────────────────────
  if (formPage.open) {
    return (
      <CaseStudyForm
        editId={formPage.editId}
        onBack={() => setFormPage({ open: false, editId: null })}
        onSaved={() => {
          setFormPage({ open: false, editId: null });
          fetchCases();
        }}
      />
    );
  }

  // ── List Page ─────────────────────────────────────────────────────────────
  return (
    <div className="max-w-[1400px] mx-auto space-y-5">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Case Studies</h1>
          <p className="page-subtitle">
            Manage client success stories published on the website
          </p>
        </div>
        <button
          onClick={() => setFormPage({ open: true, editId: null })}
          className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg text-white"
          style={{ background: "hsl(var(--accent))" }}
        >
          <Plus className="w-4 h-4" /> Add Case Study
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total", value: cases.length, color: "hsl(var(--accent))" },
          {
            label: "Active",
            value: cases.filter((c) => c.status === 1).length,
            color: "hsl(var(--success))",
          },
          {
            label: "Inactive",
            value: cases.filter((c) => c.status !== 1).length,
            color: "hsl(var(--warning))",
          },
          {
            label: "Industries",
            value: industries.length - 1,
            color: "hsl(var(--info))",
          },
        ].map((s) => (
          <div key={s.label} className="cms-card p-4">
            <p className="text-2xl font-bold" style={{ color: s.color }}>
              {s.value}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="cms-card p-4 flex flex-wrap items-center gap-3">
        <div className="search-input flex-1 min-w-[200px] max-w-sm">
          <Search className="w-4 h-4" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title, industry or solution..."
            className="flex-1 bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {industries.map((ind) => (
            <button
              key={ind}
              onClick={() => setIndustryFilter(ind)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                industryFilter === ind
                  ? "text-white border-transparent"
                  : "border-border text-muted-foreground hover:border-accent/30"
              }`}
              style={
                industryFilter === ind
                  ? { background: "hsl(var(--accent))" }
                  : {}
              }
            >
              {ind}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="section-panel">
        <div className="overflow-x-auto">
          <table className="w-full cms-table">
            <thead>
              <tr>
                <th>Case Study</th>
                <th>Industry</th>
                <th>Solution</th>
                <th>Timeline</th>
                <th>Results</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {listLoading ? (
                <tr>
                  <td
                    colSpan={7}
                    className="text-center py-10 text-muted-foreground text-sm"
                  >
                    Loading...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="text-center py-10 text-muted-foreground text-sm"
                  >
                    No case studies found
                  </td>
                </tr>
              ) : (
                filtered.map((c) => (
                  <tr key={c.primary_id} className="group">
                    <td>
                      <div className="flex items-center gap-3">
                        {c.hero_image ? (
                          <img
                            src={c.hero_image}
                            alt={c.title}
                            className="w-10 h-10 rounded-lg object-cover flex-shrink-0 border border-border"
                          />
                        ) : (
                          <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{
                              background: "hsl(var(--accent)/0.12)",
                              color: "hsl(var(--accent))",
                            }}
                          >
                            <Briefcase className="w-4 h-4" />
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-foreground text-sm line-clamp-1">
                            {c.title}
                          </p>
                          <p className="text-xs text-muted-foreground line-clamp-1">
                            {c.summary}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <Building2 className="w-3.5 h-3.5" />
                        {c.industry}
                      </span>
                    </td>
                    <td className="text-sm text-foreground font-medium">
                      {c.solution}
                    </td>
                    <td>
                      {c.timeline && (
                        <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          <Clock className="w-3.5 h-3.5" />
                          {c.timeline}
                        </span>
                      )}
                    </td>
                    <td>
                      {c.results?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {c.results.slice(0, 2).map((r, i) => (
                            <span
                              key={i}
                              className="text-xs font-bold px-2 py-0.5 rounded-full"
                              style={{
                                background: "hsl(var(--accent)/0.12)",
                                color: "hsl(var(--accent))",
                              }}
                            >
                              {r.metric}
                            </span>
                          ))}
                          {c.results.length > 2 && (
                            <span className="text-xs text-muted-foreground">
                              +{c.results.length - 2}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td>
                      {c.status === 1 ? (
                        <span
                          className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold"
                          style={{
                            background: "hsl(var(--success-muted))",
                            color: "hsl(var(--success))",
                          }}
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-current" />{" "}
                          Active
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold"
                          style={{
                            background: "hsl(var(--destructive-muted))",
                            color: "hsl(var(--destructive))",
                          }}
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-current" />{" "}
                          Inactive
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setViewCase(c)}
                          className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground"
                          title="Preview"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggle(c)}
                          className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted"
                          style={{
                            color:
                              c.status === 1
                                ? "hsl(var(--success))"
                                : "hsl(var(--warning))",
                          }}
                          title="Toggle"
                        >
                          {c.status === 1 ? (
                            <ToggleRight className="w-4 h-4" />
                          ) : (
                            <ToggleLeft className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() =>
                            setFormPage({ open: true, editId: c.primary_id })
                          }
                          className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteId(c.primary_id)}
                          className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted"
                          style={{ color: "hsl(var(--destructive))" }}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-border">
          <p className="text-xs text-muted-foreground">
            Showing {filtered.length} of {cases.length} case studies
          </p>
        </div>
      </div>

      {/* Preview Modal */}
      {viewCase && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 backdrop-blur-sm p-4"
          onClick={() => setViewCase(null)}
        >
          <div
            className="section-panel p-0 w-full max-w-lg max-h-[90vh] overflow-y-auto fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="section-panel-header sticky top-0 z-10">
              <h2 className="section-panel-title">Case Study Preview</h2>
              <button
                onClick={() => setViewCase(null)}
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="section-panel-body space-y-4">
              {viewCase.hero_image && (
                <img
                  src={viewCase.hero_image}
                  alt={viewCase.title}
                  className="w-full h-44 object-cover rounded-lg"
                />
              )}
              <div className="flex flex-wrap gap-2">
                <span
                  className="px-2 py-0.5 rounded-full text-xs font-semibold text-white"
                  style={{ background: "hsl(var(--accent))" }}
                >
                  {viewCase.industry}
                </span>
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold border border-border text-muted-foreground">
                  {viewCase.solution}
                </span>
              </div>
              <h2 className="text-lg font-bold text-foreground">
                {viewCase.title}
              </h2>
              <p className="text-sm text-muted-foreground">
                {viewCase.summary}
              </p>
              {viewCase.results?.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {viewCase.results.map((r, i) => (
                    <div
                      key={i}
                      className="p-3 rounded-lg text-center border border-border bg-muted/30"
                    >
                      <p
                        className="text-lg font-bold"
                        style={{ color: "hsl(var(--accent))" }}
                      >
                        {r.metric}
                      </p>
                      <p className="text-xs text-muted-foreground">{r.label}</p>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-4 text-xs text-muted-foreground py-2 border-y border-border">
                {viewCase.timeline && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {viewCase.timeline}
                  </span>
                )}
                {viewCase.team_size && (
                  <span className="flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {viewCase.team_size}
                  </span>
                )}
              </div>
              <div className="flex justify-end pt-1">
                <button
                  onClick={() => {
                    setViewCase(null);
                    setFormPage({ open: true, editId: viewCase.primary_id });
                  }}
                  className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg border border-border hover:bg-muted text-foreground"
                >
                  <Pencil className="w-4 h-4" /> Edit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 backdrop-blur-sm"
          onClick={() => setDeleteId(null)}
        >
          <div
            className="section-panel p-0 w-full max-w-sm mx-4 fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="section-panel-header">
              <h2 className="section-panel-title">Delete Case Study</h2>
            </div>
            <div className="section-panel-body space-y-4">
              <p className="text-sm text-muted-foreground">
                Are you sure? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteId(null)}
                  className="px-4 py-2 rounded-lg text-sm font-semibold border border-border text-foreground hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
                  style={{ background: "hsl(var(--destructive))" }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
